// Framework-free i18n core: the language store (observable, so React and the
// service worker can both read it), the `translate()` lookup with `{param}`
// interpolation, and the plural-category rule good enough for Russian.
//
// EN is the source of truth (see en.ts); RU is compile-checked against it.
// The default setting is 'system', resolved from navigator.language.

import { en, type Dictionary, type MessageKey, type MessageParams, type PluralForms } from './en.js';
import { ru } from './ru.js';

export type { MessageKey, MessageParams } from './en.js';

export type LangSetting = 'system' | 'en' | 'ru';
export type Lang = 'en' | 'ru';

/** KV key holding the persisted language choice (docs/06 §2 Settings). */
export const LANG_KEY = 'app-language';

const DICTS: Record<Lang, Dictionary> = { en, ru };

export function resolveLang(setting: LangSetting): Lang {
  if (setting === 'en' || setting === 'ru') return setting;
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'en';
  return nav?.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

// --- Observable store --------------------------------------------------------

let currentSetting: LangSetting = 'system';
let currentLang: Lang = resolveLang(currentSetting);
const listeners = new Set<() => void>();

export function getSetting(): LangSetting {
  return currentSetting;
}
export function getLang(): Lang {
  return currentLang;
}
export function setLanguageSetting(setting: LangSetting): void {
  if (setting === currentSetting) return;
  currentSetting = setting;
  currentLang = resolveLang(setting);
  for (const listener of [...listeners]) listener();
}
export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// --- Plural rule -------------------------------------------------------------

/** CLDR plural category for `n`. English: one/other; Russian: one/few/many. */
export function pluralCategory(lang: Lang, n: number): keyof PluralForms {
  const abs = Math.abs(Math.trunc(n));
  if (lang === 'ru') {
    const mod10 = abs % 10;
    const mod100 = abs % 100;
    if (mod10 === 1 && mod100 !== 11) return 'one';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few';
    return 'many';
  }
  return abs === 1 ? 'one' : 'other';
}

// --- Translation -------------------------------------------------------------

function interpolate(template: string, params?: MessageParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in params ? String(params[key]) : whole,
  );
}

export function translate(lang: Lang, key: MessageKey, params?: MessageParams): string {
  const value = DICTS[lang][key] ?? en[key];
  if (typeof value === 'string') return interpolate(value, params);
  const count = Number(params?.count ?? 0);
  const template = value[pluralCategory(lang, count)] ?? value.other;
  return interpolate(template, params);
}
